import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link as LinkIcon, X, Download, Sparkles, AlertCircle } from "lucide-react";

export default function ImportDialog({ onClose, onImported }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [category, setCategory] = useState("");

  const fetchPreview = async () => {
    if (!url) return;
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/import/url", { url });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch URL. Some sites (Etsy) block server fetches — paste the details manually instead.");
    } finally {
      setLoading(false);
    }
  };

  const addToCatalog = async () => {
    if (!preview) return;
    if (!category) { toast.error("Pick a category first"); return; }
    if (!preview.image_url) { toast.error("No image found. Add one manually on the next step."); return; }
    try {
      await api.post("/products", {
        title: preview.title,
        description: preview.description || preview.title,
        price: preview.price,
        currency: preview.currency,
        image_url: preview.image_url,
        affiliate_url: preview.affiliate_url,
        platform: preview.platform,
        category,
        tags: [],
        featured: false,
      });
      toast.success("Added to catalog");
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="import-dialog">
      <div className="admin-card w-full max-w-2xl max-h-[90vh] overflow-auto admin-scroll">
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#121212]">
          <div>
            <h2 className="font-display font-black text-2xl tracking-tighter">Quick Import</h2>
            <p className="text-xs text-white/50 mt-1">Paste any product URL from Amazon, Etsy, Shopify, or Shop.com.</p>
          </div>
          <button data-testid="close-import" onClick={onClose} className="text-white/60 hover:text-white"><X /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 border border-white/10 px-3 bg-[#0A0A0A]">
              <LinkIcon className="w-4 h-4 text-white/40" />
              <input
                data-testid="import-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.amazon.com/dp/..."
                className="flex-1 bg-transparent py-3 outline-none text-sm"
              />
            </div>
            <button
              data-testid="import-fetch"
              onClick={fetchPreview}
              disabled={loading || !url}
              className="px-5 bg-[#E53935] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#C62828] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> {loading ? "Fetching…" : "Fetch"}
            </button>
          </div>

          <div className="text-xs text-white/40 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Etsy aggressively blocks server scrapers — if it fails, use the manual <em>New product</em> form. Amazon and most Shopify stores work fine.</span>
          </div>

          {preview && (
            <div className="border-t border-white/10 pt-6 space-y-4" data-testid="import-preview">
              <div className="flex gap-4">
                {preview.image_url ? (
                  <img src={preview.image_url} alt="" className="w-24 h-24 object-cover bg-white/5" />
                ) : (
                  <div className="w-24 h-24 bg-white/5 flex items-center justify-center text-xs text-white/40">No image</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="overline text-[#E53935]">{preview.platform}</p>
                  <h3 className="font-semibold mt-1 line-clamp-2">{preview.title}</h3>
                  <p className="text-lg font-display font-black tracking-tighter mt-1">${preview.price?.toFixed(2)}</p>
                </div>
              </div>

              <textarea
                data-testid="import-desc"
                value={preview.description}
                onChange={(e) => setPreview({ ...preview, description: e.target.value })}
                rows={3}
                className="w-full bg-[#0A0A0A] border border-white/10 p-3 text-xs outline-none focus:border-[#E53935]"
                placeholder="Description (you can edit)"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-white/40 block mb-2">Category</label>
                  <input
                    data-testid="import-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Home & Decor"
                    className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#E53935]"
                  />
                </div>
                <div>
                  <label className="overline text-white/40 block mb-2">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={preview.price}
                    onChange={(e) => setPreview({ ...preview, price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#E53935]"
                  />
                </div>
              </div>

              <div className="text-xs text-white/40 break-all">
                <strong className="text-white/60">Affiliate URL:</strong> {preview.affiliate_url}
              </div>

              <button
                data-testid="import-confirm"
                onClick={addToCatalog}
                className="w-full bg-[#E53935] text-white py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C62828] inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Add to Catalog
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
